/**
 * DocxPreviewOverlay - In-app DOCX preview using docx-preview (docxjs).
 *
 * Renders .docx files as paginated HTML using the docx-preview library.
 * Loaded from a Uint8Array (via IPC) and converted to HTML in-browser.
 * Follows the same pattern as PDFPreviewOverlay.
 */

import { useState, useCallback, useMemo, useEffect, useRef } from 'react'
import { FileText } from 'lucide-react'
import { PreviewOverlay } from './PreviewOverlay'
import { CopyButton } from './CopyButton'
import { ItemNavigator } from './ItemNavigator'

interface PreviewItem {
  src: string
  label?: string
}

export interface DocxPreviewOverlayProps {
  isOpen: boolean
  onClose: () => void
  /** Absolute file path for the DOCX (single item / backward compat) */
  filePath: string
  /** Multiple items for arrow navigation */
  items?: PreviewItem[]
  /** Initial active item index (defaults to 0) */
  initialIndex?: number
  /** Async loader that returns DOCX data as Uint8Array */
  loadDocxData: (path: string) => Promise<Uint8Array>
  theme?: 'light' | 'dark'
}

export function DocxPreviewOverlay({
  isOpen,
  onClose,
  filePath,
  items,
  initialIndex = 0,
  loadDocxData,
  theme = 'light',
}: DocxPreviewOverlayProps) {
  // Normalize: items array or single filePath
  const resolvedItems = useMemo<PreviewItem[]>(() => {
    if (items && items.length > 0) return items
    return [{ src: filePath }]
  }, [items, filePath])

  const [activeIdx, setActiveIdx] = useState(initialIndex)
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const renderTargetRef = useRef<HTMLDivElement>(null)

  const activeItem = resolvedItems[activeIdx]

  // Reset index when overlay opens
  useEffect(() => {
    if (isOpen) {
      setActiveIdx(initialIndex)
    }
  }, [isOpen, initialIndex])

  // Load and render DOCX when overlay opens or active item changes
  useEffect(() => {
    if (!isOpen || !activeItem?.src) return

    let cancelled = false
    setIsLoading(true)
    setError(null)

    // Clear previous render
    if (renderTargetRef.current) {
      renderTargetRef.current.innerHTML = ''
    }

    loadDocxData(activeItem.src)
      .then(async (data) => {
        if (cancelled) return
        // Dynamic import to keep the bundle lean when docx preview isn't used
        const { renderAsync } = await import('docx-preview')
        if (cancelled || !renderTargetRef.current) return

        await renderAsync(data, renderTargetRef.current, undefined, {
          className: 'docx-preview',
          inWrapper: true,
          ignoreWidth: false,
          ignoreHeight: false,
          ignoreFonts: false,
          breakPages: true,
          ignoreLastRenderedPageBreak: false,
          experimental: true,
          renderHeaders: true,
          renderFooters: true,
          renderFootnotes: true,
          renderEndnotes: true,
        })

        if (!cancelled) {
          setIsLoading(false)
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load DOCX')
          setIsLoading(false)
        }
      })

    return () => { cancelled = true }
  }, [isOpen, activeItem?.src, loadDocxData])

  // Scroll to top when active item changes
  useEffect(() => {
    if (!isOpen) return
    requestAnimationFrame(() => {
      containerRef.current?.scrollTo({ top: 0, left: 0 })
    })
  }, [isOpen, activeItem?.src])

  // Header actions: item navigation + copy button
  const headerActions = (
    <div className="flex items-center gap-2">
      <ItemNavigator items={resolvedItems} activeIndex={activeIdx} onSelect={setActiveIdx} size="md" />
      <CopyButton content={activeItem?.src || filePath} title="Copy path" className="bg-background shadow-minimal" />
    </div>
  )

  return (
    <PreviewOverlay
      isOpen={isOpen}
      onClose={onClose}
      theme={theme}
      typeBadge={{
        icon: FileText,
        label: 'DOCX',
        variant: 'blue',
      }}
      filePath={activeItem?.src || filePath}
      error={error ? { label: 'Load Failed', message: error } : undefined}
      headerActions={headerActions}
    >
      <div ref={containerRef} className="h-full overflow-auto overscroll-contain">
        {isLoading && (
          <div className="h-full flex items-center justify-center text-muted-foreground text-sm">Loading document...</div>
        )}
        <div
          ref={renderTargetRef}
          className="docx-preview-container min-h-full"
          style={{
            // Style the rendered docx pages with consistent spacing
            ...(isLoading ? { display: 'none' } : {}),
          }}
        />
      </div>
      {/* Scoped styles for docx-preview rendered output */}
      <style>{`
        .docx-preview-container .docx-wrapper {
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 16px;
          gap: 16px;
          background: ${theme === 'dark' ? 'hsl(var(--background))' : 'hsl(var(--muted))'};
          min-height: 100%;
        }
        .docx-preview-container .docx-wrapper > section.docx {
          background: ${theme === 'dark' ? 'hsl(var(--card))' : '#fff'};
          box-shadow: 0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1);
          border-radius: 4px;
          overflow: hidden;
        }
        .docx-preview-container .docx-wrapper > section.docx * {
          color: ${theme === 'dark' ? 'hsl(var(--foreground))' : 'inherit'};
        }
      `}</style>
    </PreviewOverlay>
  )
}
